import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  ClipboardList, 
  Activity, 
  Brain, 
  FileText, 
  Download, 
  AlertTriangle, 
  CheckCircle, 
  ArrowLeft, 
  ArrowRight,
  Printer,
  FileJson,
  FileText as FileTextIcon,
  FileUp,
  XCircle,
  Copy,
  Sparkles
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import ClinicalSymptomsForm, { ClinicalSymptomsData } from './ClinicalSymptomsForm';
import NeuroConductionForm from './NeuroConductionForm';
import EMGNeedleAnalysis, { EMGNerveRecord } from './EMGNeedleAnalysis';
import PrintableEdxReport from './PrintableEdxReport';
import { JsonReportGenerator, ClinicalReport, EMGMuscleResult } from '../services/jsonReportGenerator';
import { DiagnosticPatternAnalyzer, DiagnosticPattern, DiagnosticRecommendation } from '../services/diagnosticPatternAnalyzer';
import {
  applyClinicalImpressionToReport,
  buildClinicalImpression,
} from '../services/clinicalImpressionBuilder';
import { buildPlainTextEdxReport } from '../services/edxReportTextBuilder';
import { evaluateEdxReportQuality, EdxReportQualityIssue } from '../services/edxReportQualityChecks';
import { Patient, Sex } from '../types/patient';
import { NCSTestResult, SpecialStudyData } from '../types/ncs';
import { ExpandedPatternAnalyzer } from '../services/expandedPatternAnalyzer';
import { hasNcsLowTemperatureRecords } from '../data/emgClinicalCriteria';
import {
  validateClinicalReportCrossConsistency,
  resolvePatientAgeForCrossValidation,
} from '../services/patternCrossValidationAdapters';
import type { CrossValidationResult } from '../services/patternCrossValidationService';
import PatternConsistencyPanel from './PatternConsistencyPanel';
import FloatingGeniePanel from './FloatingGeniePanel';
import {
  buildGenieOrientation,
  evaluateGenie,
  getActiveTiebreakers,
  getGenieWarnings,
  inferGenieAnswersFromNcs,
  patternMatchesSuspicion,
  type GenieAnswers,
  type GenieEvaluation,
  type LiveDiagnosis,
} from '../services/genieEngine';
import { storageService, WorkflowDraft } from '../services/unifiedStorageService';

interface CompleteClinicalWorkflowProps {
  patient: Patient;
  initialFormData?: any; // 🔥 DATOS MAPEADOS DESDE ARCHIVO
  sourceFileName?: string;
  restoredDraft?: WorkflowDraft | null;
  onComplete?: (result: WorkflowResult) => void;
  onBack?: () => void;
}

interface WorkflowResult {
  clinicalReport: ClinicalReport;
  diagnosticPatterns: DiagnosticPattern[];
  recommendations: DiagnosticRecommendation[];
  jsonReport: string;
  crossValidation?: CrossValidationResult | null;
}

type EmgStatus = EMGNerveRecord[] | 'skipped' | null;

type WorkflowStep = 'patientInfo' | 'symptoms' | 'ncs' | 'emg' | 'analysis' | 'report';

/** Validación cruzada EMG paravertebral vs SNAP (radiculopatía vs plexopatía). */
function validateRadiculopathyProtocol(
  report: ClinicalReport
): { severity: 'warning'; message: string } | null {
  const primary = report.conclusion?.primaryDiagnosis ?? '';
  const isRadiculopathySuspected = primary.toLowerCase().includes('radiculopat');
  const hasParaspinalData =
    report.electrophysiologicalData.emg?.muscles.some(m => {
      const n = m.muscleName.toLowerCase();
      return (
        n.includes('paravertebral') ||
        n.includes('paraspinal') ||
        n.includes('multifidus')
      );
    }) ?? false;

  if (isRadiculopathySuspected && !hasParaspinalData) {
    return {
      severity: 'warning',
      message:
        'Atención: Ha diagnosticado Radiculopatía sin registrar datos de músculos paravertebrales. Se recomienda explorarlos para descartar Plexopatía de forma definitiva.',
    };
  }
  const hasParaspinalDenervation =
    report.electrophysiologicalData.emg?.muscles.some(m => {
      const n = m.muscleName.toLowerCase();
      const isParaspinal =
        n.includes('paravertebral') ||
        n.includes('paraespinal') ||
        n.includes('paraspinal') ||
        n.includes('multifidus');
      const spontaneous = m.spontaneousActivitySummary.toLowerCase();
      return isParaspinal && spontaneous !== 'ausente' && spontaneous !== 'normal';
    }) ?? false;
  const sensoryNcs = report.electrophysiologicalData.ncs?.results.filter(r => r.type === 'sensory') ?? [];
  const hasNormalSnap = sensoryNcs.some(r => r.status === 'normal');
  const hasAbnormalSnap = sensoryNcs.some(r => r.status === 'abnormal');

  if (hasParaspinalDenervation && hasNormalSnap && !hasAbnormalSnap) {
    return {
      severity: 'warning',
      message:
        'SNAPs sensitivos conservados con denervación paravertebral: patrón compatible con lesión preganglionar/radiculopatía si la distribución miotómica es concordante. Correlacione con clínica e imagen.',
    };
  }

  if (hasParaspinalDenervation && sensoryNcs.length === 0) {
    return {
      severity: 'warning',
      message:
        'Hay denervación paravertebral, pero no se registraron SNAPs sensitivos. Complete neuroconducciones sensitivas relevantes antes de distinguir radiculopatía pura de plexopatía.',
    };
  }
  return null;
}

const FormattedReportDisplay: React.FC<{ report: ClinicalReport, patient: Patient }> = ({ report, patient }) => {

  const renderNCSValue = (label: string, value?: string | number, unit?: string, isAbnormal?: boolean) => {
    if (value === undefined || value === null || value === '') return null;
    return (
      <p className="text-sm"><strong className="text-gray-400">{label}:</strong> <span className={isAbnormal ? 'text-red-400 font-semibold' : 'text-gray-200'}>{value}</span>{unit ? ` ${unit}` : ''}</p>
    );
  };

  return (
    <div className="prose prose-sm sm:prose lg:prose-lg xl:prose-xl dark:prose-invert bg-gray-800/50 backdrop-blur-sm p-4 md:p-6 rounded-lg border border-gray-700 max-w-none overflow-auto text-gray-300">
      <h2 className="text-2xl font-bold text-gray-100 border-b pb-2 mb-4 border-gray-600">Reporte Clínico Detallado</h2>
      
      <section className="mb-6">
        <h3 className="text-xl font-semibold text-gray-200 mb-3 border-b border-gray-700 pb-1">Información del Paciente</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <p><strong>ID:</strong> {patient.id}</p>
            <p><strong>Nombre:</strong> {patient.firstName} {patient.lastName}</p>
            <p><strong>Fecha de Nacimiento:</strong> {patient.dateOfBirth}</p>
            <p><strong>Edad:</strong> {report.patient.demographics.age} años</p>
            <p><strong>Sexo:</strong> {patient.sex}</p>
            <p className="md:col-span-2"><strong>Motivo de Consulta:</strong> {report.patient.consultReason}</p>
        </div>
      </section>

      {report.clinicalSymptoms && (
        <section className="mb-6">
          <h3 className="text-xl font-semibold text-gray-200 mb-3 border-b border-gray-700 pb-1">Resumen de Síntomas</h3>
          {(Object.entries(report.clinicalSymptoms) as Array<[
            keyof ClinicalReport['clinicalSymptoms'],
            ClinicalReport['clinicalSymptoms'][keyof ClinicalReport['clinicalSymptoms']]
          ]>).map(([key, category]) => (
            category.present && (
                <div key={key} className="mb-2 text-sm">
                    <strong className="capitalize text-gray-100">{key}:</strong> {category.count} {category.count === 1 ? 'síntoma' : 'síntomas'} (Severidad General: {category.severity}).
                    <ul className="list-disc pl-6 mt-1 text-gray-400">
                        {category.symptoms.filter(s => s.present).map(s => <li key={s.name} className="text-xs">{s.name} ({s.severity}) - {s.locations?.join(', ') || 'N/A'}</li>)}
                    </ul>
                </div>
            )
          ))}
        </section>
      )}

      {report.electrophysiologicalData.ncs && report.electrophysiologicalData.ncs.results.length > 0 && (
        <section className="mb-6">
          <h3 className="text-xl font-semibold text-gray-200 mb-3 border-b border-gray-700 pb-1">Hallazgos de Neuroconducción (NCS)</h3>
          {hasNcsLowTemperatureRecords(report.electrophysiologicalData.ncs.results) && (
            <div className="bg-yellow-900/40 border border-yellow-600 p-4 rounded-md mb-4">
              <div className="flex items-center text-yellow-400">
                <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
                <span className="font-bold">Aviso de precisión térmica</span>
              </div>
              <p className="text-sm text-yellow-200 mt-1">
                Se detectaron registros con temperatura cutánea bajo el estándar del territorio. Pueden aplicarse correcciones
                matemáticas en el análisis; si los valores son limítrofes, caliente las extremidades y considere repetir la prueba.
              </p>
            </div>
          )}
          <div className="space-y-3">
            {report.electrophysiologicalData.ncs.results.map((ncs: NCSTestResult, index) => (
              <div key={index} className="p-3 border border-gray-600 rounded bg-gray-700/40">
                <p className="font-semibold text-gray-100">{ncs.nerve} ({ncs.side}) - Estado: <span className={`${ncs.status && ncs.status.toLowerCase() === 'abnormal' ? 'text-red-400 font-bold' : 'text-green-400'}`}>{ncs.status || 'N/A'}</span></p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 mt-1">
                  {renderNCSValue('Latencia', ncs.latency, 'ms', ncs.abnormalParameters?.includes('latency'))}
                  {renderNCSValue('Amplitud', ncs.amplitude, ncs.amplitudeUnit, ncs.abnormalParameters?.includes('amplitude'))}
                  {renderNCSValue('Velocidad', ncs.velocity, 'm/s', ncs.abnormalParameters?.includes('velocity'))}
                  {ncs.fWave !== undefined && renderNCSValue('Onda F', ncs.fWave, 'ms', ncs.abnormalParameters?.includes('fWave'))}
                </div>
                {ncs.findings && ncs.findings.length > 0 && <p className="text-xs text-gray-400 mt-1">Interpretación: {ncs.findings.join('. ')}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {report.electrophysiologicalData.emg ? (
        <section className="mb-6">
          <h3 className="text-xl font-semibold text-gray-200 mb-3 border-b border-gray-700 pb-1">Hallazgos de Electromiografía (EMG)</h3>
          {report.electrophysiologicalData.emg.status === 'skipped' ? (
            <p className="italic text-gray-400 p-3 bg-gray-700/30 rounded-md">Estudio de Electromiografía (EMG) no realizado.</p>
          ) : report.electrophysiologicalData.emg.status === 'nodata' || !report.electrophysiologicalData.emg.muscles || report.electrophysiologicalData.emg.muscles.length === 0 ? (
            <p className="italic text-gray-400 p-3 bg-gray-700/30 rounded-md">No se registraron datos de EMG o el estudio no fue completado.</p>
          ) : (
            <div className="space-y-3">
            {report.electrophysiologicalData.emg.muscles.map((muscle: EMGMuscleResult, index) => (
              <div key={index} className="p-3 border border-gray-600 rounded bg-gray-700/40">
                <p className="font-semibold text-gray-100">{muscle.muscleName} ({muscle.side})</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 mt-1 text-sm">
                    <p><strong className="text-gray-400">Act. Inserción:</strong> <span className={muscle.insertionalActivity !== 'normal' && muscle.insertionalActivity !=='no especificada' ? 'text-yellow-400' : 'text-gray-200' }>{muscle.insertionalActivity}</span></p>
                    <p><strong className="text-gray-400">Act. Espontánea:</strong> <span className={muscle.spontaneousActivitySummary !== 'Ausente' && muscle.spontaneousActivitySummary !=='Normal' ? 'text-yellow-400' : 'text-gray-200' }>{muscle.spontaneousActivitySummary}</span></p>
                    <div><strong className="text-gray-400">PUMs:</strong>
                        <ul className="list-disc list-inside pl-4 text-gray-300">
                            <li>Amplitud: {muscle.mupAmplitude}µV</li>
                            <li>Duración: {muscle.mupDuration}ms</li>
                            <li>Polifasia: {muscle.mupPolyphasia}%</li>
                        </ul>
                    </div>
                    <p><strong className="text-gray-400">Reclutamiento:</strong> <span className={muscle.recruitmentPattern !== 'normal' && muscle.recruitmentPattern !=='no especificado' ? 'text-yellow-400' : 'text-gray-200' }>{muscle.recruitmentPattern}</span></p>
                </div>
                {muscle.interpretationNotes && <p className="text-xs text-gray-500 mt-2">Notas Específicas: {muscle.interpretationNotes}</p>}
              </div>
            ))}
            </div>
          )}
           {report.electrophysiologicalData.emg.globalFindings && report.electrophysiologicalData.emg.status === 'performed' && (
            <div className="mt-4 p-3 bg-gray-700/20 rounded-md border border-gray-600/50 text-sm">
                <h4 className="font-medium text-gray-300 mb-1">Hallazgos Globales EMG:</h4>
                <p><strong className="text-gray-400">Patrón General:</strong> {report.electrophysiologicalData.emg.globalFindings.overallPattern}</p>
                <p><strong className="text-gray-400">Distribución:</strong> {report.electrophysiologicalData.emg.globalFindings.distribution}</p>
                <p><strong className="text-gray-400">Severidad:</strong> {report.electrophysiologicalData.emg.globalFindings.severity}</p>
                <p><strong className="text-gray-400">Cronicidad:</strong> {report.electrophysiologicalData.emg.globalFindings.chronicity}</p>
            </div>
            )}
        </section>
      ) : null}
      
      {report.analysis && (report.analysis.electrophysiologicalPatterns.length > 0 || report.analysis.riskFactors.length > 0 || report.analysis.redFlags.length >0 ) && (
         <section className="mb-6">
          <h3 className="text-xl font-semibold text-gray-200 mb-3 border-b border-gray-700 pb-1">Análisis y Puntuaciones</h3>
           {report.analysis.electrophysiologicalPatterns.length > 0 && <div className="mb-2"><strong className="text-gray-400">Patrones Electrofisiológicos:</strong> {report.analysis.electrophysiologicalPatterns.map(p => p.patternName).join(', ')}</div>}
           {/* Could add SymptomPatternScore display here */}
           {report.analysis.redFlags.length > 0 && <div className="text-red-400"><strong className="text-red-300">Banderas Rojas:</strong> {report.analysis.redFlags.map(rf => rf.flag).join(', ')}</div>}
        </section>
      )}

      {report.conclusion && (
        <section className="mb-6">
          <h3 className="text-xl font-semibold text-gray-200 mb-3 border-b border-gray-700 pb-1">Conclusión</h3>
          <p><strong>Diagnóstico Principal:</strong> <span className="text-blue-300 font-semibold">{report.conclusion.primaryDiagnosis}</span></p>
          <p><strong>Confianza Diagnóstica:</strong> {report.conclusion.confidence * 100}%</p>
          <p><strong>Nivel de Urgencia:</strong> <span className={`${report.conclusion.urgency === 'urgent' ? 'text-red-400 font-bold' : report.conclusion.urgency === 'high' ? 'text-orange-400' : 'text-gray-300'}`}>{report.conclusion.urgency.toUpperCase()}</span></p>
          {report.conclusion.differentialDiagnoses && report.conclusion.differentialDiagnoses.length > 0 &&
            <p><strong className="text-gray-400">Diagnósticos Diferenciales:</strong> {report.conclusion.differentialDiagnoses.join(', ')}</p> }
          <h4 className="text-lg font-medium text-gray-300 mt-3 mb-1">Recomendaciones:</h4>
          <ul className="list-disc pl-5 text-gray-300 space-y-1 text-sm">
            {report.conclusion.recommendations.map((rec, i) => <li key={i}>{rec}</li>)}
          </ul>
        </section>
      )}
      <hr className="border-gray-700 my-6"/>
      <p className="text-xs text-gray-500 text-center">Reporte generado el: {new Date(report.timestamp).toLocaleString()} por la versión {report.version} del sistema.</p>
    </div>
  );
};

const ReportQualityPanel: React.FC<{ issues: EdxReportQualityIssue[] }> = ({ issues }) => {
  const severityStyles = {
    error: 'bg-red-900/35 border-red-700 text-red-100',
    warning: 'bg-amber-900/35 border-amber-700 text-amber-100',
    info: 'bg-blue-900/25 border-blue-700 text-blue-100',
  };
  const counts = {
    error: issues.filter(issue => issue.severity === 'error').length,
    warning: issues.filter(issue => issue.severity === 'warning').length,
    info: issues.filter(issue => issue.severity === 'info').length,
  };

  return (
    <section className="mb-6 no-print bg-gray-800/60 border border-gray-700 rounded-lg p-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-100">Calidad del informe</h3>
          <p className="text-sm text-gray-400">Advertencias de trazabilidad técnica antes de imprimir o copiar.</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2 py-1 rounded bg-red-900/40 text-red-100">Errores: {counts.error}</span>
          <span className="px-2 py-1 rounded bg-amber-900/40 text-amber-100">Advertencias: {counts.warning}</span>
          <span className="px-2 py-1 rounded bg-blue-900/40 text-blue-100">Notas: {counts.info}</span>
        </div>
      </div>
      {issues.length === 0 ? (
        <div className="flex items-center text-green-200 text-sm">
          <CheckCircle className="w-4 h-4 mr-2" />
          No se detectaron problemas de calidad del reporte.
        </div>
      ) : (
        <div className="space-y-2">
          {issues.map(issue => (
            <div key={issue.id} className={`border rounded-md p-3 ${severityStyles[issue.severity]}`}>
              <p className="font-medium">{issue.title}</p>
              <p className="text-sm opacity-90">{issue.message}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const CompleteClinicalWorkflow: React.FC<CompleteClinicalWorkflowProps> = ({
  patient,
  initialFormData, // 🔥 DATOS MAPEADOS DESDE ARCHIVO
  sourceFileName,
  restoredDraft,
  onComplete,
  onBack
}) => {
  const navigate = useNavigate();
  const draftIdRef = React.useRef(restoredDraft?.id ?? crypto.randomUUID());
  const [currentStep, setCurrentStep] = useState<WorkflowStep>(restoredDraft?.currentStep ?? 'patientInfo');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editablePatient, setEditablePatient] = useState<Patient>(() => ({
    ...(restoredDraft?.patient ?? patient),
    contact: (restoredDraft?.patient ?? patient).contact ?? { phone: '', email: '', address: '' },
    medicalHistory: (restoredDraft?.patient ?? patient).medicalHistory ?? {
      previousDiseases: [],
      surgeries: [],
      currentMedications: [],
      allergies: [],
      familyHistory: '',
    },
  }));

  // 🔥 LOGGING DE DATOS INICIALES
  React.useEffect(() => {
    if (initialFormData) {
      console.log('🎯 CompleteClinicalWorkflow recibió datos iniciales:', {
        hasSymptoms: !!initialFormData.symptoms,
        symptomsSuccess: initialFormData.symptoms?.success,
        symptomsDataLength: initialFormData.symptoms?.data ? Object.keys(initialFormData.symptoms.data).length : 0,
        overallSuccess: initialFormData.overall?.success,
        totalLogs: initialFormData.overall?.logs?.length || 0,
        warnings: initialFormData.overall?.warnings?.length || 0,
        errors: initialFormData.overall?.errors?.length || 0
      });

      // Mostrar datos de síntomas mapeados
      if (initialFormData.symptoms?.data) {
        console.group('📋 Datos de síntomas mapeados');
        Object.entries(initialFormData.symptoms.data).forEach(([category, symptoms]) => {
          console.log(`${category}:`, symptoms);
        });
        console.groupEnd();
      }
    } else {
      console.log('⚠️ CompleteClinicalWorkflow NO recibió datos iniciales - modo manual');
    }
  }, [initialFormData]);

  const [symptomsData, setSymptomsData] = useState<ClinicalSymptomsData | null>(restoredDraft?.symptomsData ?? null);
  const [genieAnswers, setGenieAnswers] = useState<GenieAnswers>(restoredDraft?.genieAnswers ?? {});
  const [genieEvaluation, setGenieEvaluation] = useState<GenieEvaluation | null>(null);
  const [initialSuspicions, setInitialSuspicions] = useState<string[]>([]);
  const [ncsResults, setNcsResults] = useState<NCSTestResult[]>(restoredDraft?.ncsResults ?? []);
  // Borrador NCS en vivo (mientras el médico edita) para el análisis del Genie.
  const [liveNcsDraft, setLiveNcsDraft] = useState<{
    ncsResults: NCSTestResult[];
    specialStudies: SpecialStudyData;
  } | null>(null);
  const [specialStudiesData, setSpecialStudiesData] = useState<SpecialStudyData>(
    restoredDraft?.specialStudiesData ?? {
      notPerformed: true,
      tests: [],
    },
  );
  const [martinGruberNcsWarning, setMartinGruberNcsWarning] = useState<string | null>(null);
  const [thermalPrecisionWarning, setThermalPrecisionWarning] = useState<string | null>(null);
  const [emgResults, setEmgResults] = useState<EmgStatus>(restoredDraft?.emgResults ?? null);
  const [clinicalReport, setClinicalReport] = useState<ClinicalReport | null>(null);
  const [diagnosticPatterns, setDiagnosticPatterns] = useState<DiagnosticPattern[]>([]);
  const [recommendations, setRecommendations] = useState<DiagnosticRecommendation[]>([]);
  const [crossValidationResult, setCrossValidationResult] = useState<CrossValidationResult | null>(null);
  
  React.useEffect(() => {
    const clinicalAnswers = initialFormData?.genieClinicalAnswers as GenieAnswers | undefined;
    if (!clinicalAnswers || Object.keys(clinicalAnswers).length === 0) return;
    setGenieAnswers(prev => {
      const next = { ...prev };
      for (const [questionId, optionId] of Object.entries(clinicalAnswers)) {
        if (!next[questionId]) next[questionId] = optionId;
      }
      return next;
    });
  }, [initialFormData?.genieClinicalAnswers]);

  React.useEffect(() => {
    const seedNcs = (initialFormData?.ncs?.data as NCSTestResult[] | undefined) ?? [];
    if (seedNcs.length === 0) return;
    setGenieAnswers(prev => {
      const inferred = inferGenieAnswersFromNcs(seedNcs);
      const next = { ...prev };
      for (const [questionId, optionId] of Object.entries(inferred)) {
        if (!next[questionId]) next[questionId] = optionId;
      }
      return next;
    });
  }, [initialFormData?.ncs?.data]);

  React.useEffect(() => {
    if (ncsResults.length === 0) return;
    setGenieAnswers(prev => {
      const inferred = inferGenieAnswersFromNcs(ncsResults);
      const next = { ...prev };
      for (const [questionId, optionId] of Object.entries(inferred)) {
        if (!next[questionId]) next[questionId] = optionId;
      }
      return next;
    });
  }, [ncsResults]);

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void storageService.saveWorkflowDraft({
        id: draftIdRef.current,
        patientId: editablePatient.id,
        sourceFileName,
        currentStep,
        patient: editablePatient,
        initialFormData,
        symptomsData,
        genieAnswers,
        ncsResults,
        specialStudiesData,
        emgResults,
        createdAt: restoredDraft?.createdAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [
    currentStep,
    editablePatient,
    emgResults,
    genieAnswers,
    initialFormData,
    ncsResults,
    restoredDraft?.createdAt,
    sourceFileName,
    specialStudiesData,
    symptomsData,
  ]);

  const reportContentRef = useRef<HTMLDivElement>(null);

  // Ventana del Genie correspondiente al paso actual del workflow
  const genieCurrentWindow = useMemo(() => {
    if (currentStep === 'symptoms') return 'symptoms' as const;
    if (currentStep === 'ncs') return 'ncs' as const;
    if (currentStep === 'emg') return 'emg' as const;
    return null;
  }, [currentStep]);

  // Desempates activos derivados de los patrones detectados en el análisis
  const activeTiebreakersForPanel = useMemo(
    () => getActiveTiebreakers(diagnosticPatterns.map(p => p.id)),
    [diagnosticPatterns]
  );

  // Diagnósticos en vivo: reusa el motor real sobre los datos disponibles
  // (síntomas + NCS borrador/confirmado + EMG) para alimentar al Genie en
  // tiempo real. El Genie solo los muestra; no altera el análisis final.
  const liveDiagnoses = useMemo<LiveDiagnosis[]>(() => {
    if (!symptomsData) return [];

    const effectiveNcs = ncsResults.length > 0 ? ncsResults : liveNcsDraft?.ncsResults ?? [];
    const effectiveSpecial =
      ncsResults.length > 0 ? specialStudiesData : liveNcsDraft?.specialStudies ?? specialStudiesData;

    // Necesitamos al menos algún dato electrofisiológico para que sea útil.
    if (effectiveNcs.length === 0 && (!emgResults || emgResults === 'skipped')) {
      return [];
    }

    try {
      const report = JsonReportGenerator.generateCompleteReport(
        editablePatient,
        symptomsData,
        emgResults,
        effectiveNcs,
        effectiveSpecial
      );
      if (Object.keys(genieAnswers).length > 0) {
        report.genieOrientation = buildGenieOrientation(genieAnswers);
      }
      const result = DiagnosticPatternAnalyzer.analyzeReport(report);
      return result.patterns
        .filter(p => p.confidence > 0.3)
        .slice(0, 6)
        .map(p => ({ id: p.id, name: p.name, confidence: p.confidence }));
    } catch (err) {
      console.warn('Análisis en vivo del Genie falló:', err);
      return [];
    }
  }, [
    symptomsData,
    ncsResults,
    liveNcsDraft,
    specialStudiesData,
    emgResults,
    genieAnswers,
    editablePatient,
  ]);

  const stepTitles = {
    patientInfo: 'Datos del Paciente',
    symptoms: 'Evaluación de Síntomas',
    ncs: 'Neuroconducciones',
    emg: 'Electromiografía',
    analysis: 'Análisis Diagnóstico',
    report: 'Reporte Final'
  };

  const stepIcons = {
    patientInfo: User,
    symptoms: ClipboardList,
    ncs: Activity,
    emg: Brain,
    analysis: AlertTriangle,
    report: FileText
  };

  const handlePatientFieldChange = (
    field: keyof Patient,
    value: string | number | null | undefined | Sex
  ) => {
    setEditablePatient(prev => ({
      ...prev,
      [field]: value,
      updatedAt: new Date().toISOString(),
    }));
  };

  const handleContactFieldChange = (field: keyof Patient['contact'], value: string) => {
    setEditablePatient(prev => ({
      ...prev,
      contact: {
        ...(prev.contact ?? { phone: '', email: '', address: '' }),
        [field]: value,
      },
      updatedAt: new Date().toISOString(),
    }));
  };

  const handleMedicalHistoryTextChange = (
    field: keyof Patient['medicalHistory'],
    value: string
  ) => {
    setEditablePatient(prev => ({
      ...prev,
      medicalHistory: {
        ...(prev.medicalHistory ?? {
          previousDiseases: [],
          surgeries: [],
          currentMedications: [],
          allergies: [],
          familyHistory: '',
        }),
        [field]: field === 'familyHistory'
          ? value
          : value.split(',').map(item => item.trim()).filter(Boolean),
      },
      updatedAt: new Date().toISOString(),
    }));
  };

  const handlePatientInfoComplete = () => {
    if (!editablePatient.id.trim() || !editablePatient.firstName.trim()) {
      setError('Complete al menos el ID y el nombre del paciente antes de continuar.');
      return;
    }

    setError(null);
    setCurrentStep('symptoms');
  };

  const handleSymptomsComplete = (data: ClinicalSymptomsData) => {
    setSymptomsData(data);
    setCurrentStep('ncs');
  };

  const handleGenieChange = useCallback((answers: GenieAnswers) => {
    setGenieAnswers(answers);
    setGenieEvaluation(evaluateGenie(answers));
  }, []);

  const handleTiebreakerSelect = useCallback(
    (tiebreakerId: string, optionId: string) => {
      const updated = { ...genieAnswers };
      if (updated[tiebreakerId] === optionId) {
        delete updated[tiebreakerId];
      } else {
        updated[tiebreakerId] = optionId;
      }
      setGenieAnswers(updated);
      setGenieEvaluation(evaluateGenie(updated));

      if (!clinicalReport) return;

      const report = {
        ...clinicalReport,
        genieOrientation: buildGenieOrientation(updated),
      };
      const analysisResult = DiagnosticPatternAnalyzer.analyzeReport(report);
      const reportWithConclusion = applyClinicalImpressionToReport(report, analysisResult);
      setClinicalReport(reportWithConclusion);
      setDiagnosticPatterns(analysisResult.patterns);
      setRecommendations(analysisResult.recommendations);
    },
    [genieAnswers, clinicalReport]
  );

  const handleNcsDraftChange = useCallback(
    (draft: { ncsResults: NCSTestResult[]; specialStudies: SpecialStudyData }) => {
      setLiveNcsDraft(draft);
      setMartinGruberNcsWarning(
        ExpandedPatternAnalyzer.getMartinGruberWorkflowWarning(
          draft.ncsResults,
          draft.specialStudies
        )
      );
      setThermalPrecisionWarning(
        ExpandedPatternAnalyzer.getThermalNcsPrecisionWarning(draft.ncsResults)
      );
    },
    []
  );

  const handleNCSComplete = (data: any) => {
    // Handle the new structure with NCS results and special studies
    if (data && typeof data === 'object' && 'ncsResults' in data) {
      setNcsResults(data.ncsResults || []);
      const sp = data.specialStudies as SpecialStudyData | undefined;
      setSpecialStudiesData(sp ?? { notPerformed: true, tests: [] });
      console.log('Special studies data:', data.specialStudies);
    } else {
      // Fallback for old structure
      const ncsDataArray: NCSTestResult[] = Array.isArray(data) ? data : [data];
      setNcsResults(ncsDataArray);
      setSpecialStudiesData({ notPerformed: true, tests: [] });
    }
    setMartinGruberNcsWarning(null);
    setThermalPrecisionWarning(null);
    setCurrentStep('emg');
  };

  const handleEMGComplete = (data: EMGNerveRecord[]) => {
    setEmgResults(data);
    if (symptomsData && ncsResults) {
        processCompleteData(symptomsData, ncsResults, data, specialStudiesData);
    }
  };

  const handleEMGNotPerformed = () => {
    setEmgResults('skipped');
    if (symptomsData && ncsResults) {
        processCompleteData(symptomsData, ncsResults, 'skipped', specialStudiesData);
    }
  };

  const processCompleteData = async (
    symptoms: ClinicalSymptomsData, 
    ncs: NCSTestResult[], 
    emg: EmgStatus,
    specialStudies: SpecialStudyData
  ) => {
    setIsProcessing(true);
    setError(null);

    try {
      console.log('Generando reporte JSON con EMG datos:', emg);
      const report = JsonReportGenerator.generateCompleteReport(
        editablePatient,
        symptoms,
        emg,
        ncs,
        specialStudies
      );

      // Adjuntar la orientación del Genie para que el analizador la consuma
      // como evidencia clínica (Genie orienta, analizador confirma).
      if (Object.keys(genieAnswers).length > 0) {
        report.genieOrientation = buildGenieOrientation(genieAnswers);
      }

      console.log('Analizando patrones diagnósticos...');
      const analysisResult = DiagnosticPatternAnalyzer.analyzeReport(report);
      const reportWithUnifiedConclusion = applyClinicalImpressionToReport(report, analysisResult);
      setClinicalReport(reportWithUnifiedConclusion);
      setDiagnosticPatterns(analysisResult.patterns);
      setRecommendations(analysisResult.recommendations);

      try {
        const crossValidation = validateClinicalReportCrossConsistency(
          reportWithUnifiedConclusion,
          resolvePatientAgeForCrossValidation(undefined, editablePatient.dateOfBirth)
        );
        setCrossValidationResult(crossValidation);
      } catch (cvErr) {
        console.error('Error en validación cruzada NCS/EMG:', cvErr);
        setCrossValidationResult(null);
      }

      setCurrentStep('analysis');

    } catch (err: any) {
      console.error('Error procesando datos:', err);
      setError(`Error al procesar los datos clínicos: ${err.message || 'Por favor, intente nuevamente.'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAnalysisComplete = () => {
    setCurrentStep('report');
  };

  const handleWorkflowComplete = () => {
    if (!clinicalReport || !onComplete) return;

    const radProtocolWarning = validateRadiculopathyProtocol(clinicalReport);
    if (
      radProtocolWarning &&
      !window.confirm(
        `${radProtocolWarning.message}\n\n¿Desea completar la evaluación de todas formas?`
      )
    ) {
      return;
    }

    const result: WorkflowResult = {
      clinicalReport,
      diagnosticPatterns,
      recommendations,
      jsonReport: JsonReportGenerator.exportAsJsonString(clinicalReport),
      crossValidation: crossValidationResult,
    };
    void storageService.deleteWorkflowDraft(draftIdRef.current);
    onComplete(result);
  };

  const downloadJsonReport = () => {
    if (clinicalReport) {
      JsonReportGenerator.downloadAsJsonFile(clinicalReport, `reporte-clinico-${editablePatient.id}-${Date.now()}.json`);
    }
  };

  const getAnalysisResultForReport = (report: ClinicalReport) => ({
    patterns: diagnosticPatterns,
    recommendations,
    summary: {
      totalPatternsIdentified: diagnosticPatterns.length,
      highConfidencePatterns: diagnosticPatterns.filter(p => p.confidence > 0.8).length,
      urgentFindings: diagnosticPatterns.filter(p => p.urgencyLevel === 'urgent').length,
      primaryDiagnosis: diagnosticPatterns[0]?.name || report.conclusion.primaryDiagnosis,
      overallComplexity:
        diagnosticPatterns.length <= 1 ? 'simple' as const :
        diagnosticPatterns.length <= 3 ? 'moderate' as const :
        'complex' as const,
      recommendedActions: recommendations.flatMap(rec => rec.nextSteps || []).slice(0, 6),
    },
  });

  const generatePlainTextReport = (report: ClinicalReport): string =>
    buildPlainTextEdxReport(report, editablePatient, getAnalysisResultForReport(report));

  const handleExportToTXT = () => {
    if (!clinicalReport) return;
    const textContent = generatePlainTextReport(clinicalReport);
    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reporte-clinico-${editablePatient.id}-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopyClinicalSummary = async () => {
    if (!clinicalReport) return;
    const clinicalImpression = buildClinicalImpression(
      clinicalReport,
      getAnalysisResultForReport(clinicalReport)
    );
    await navigator.clipboard.writeText(clinicalImpression.copySummary);
  };

  const handleExportToPDF = () => {
    if (!reportContentRef.current || !clinicalReport) {
      console.error("No hay contenido del reporte para exportar a PDF o el reporte no está disponible.");
      return;
    }
    html2canvas(reportContentRef.current, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#111827' // Dark background for canvas similar to bg-gray-900
    } as any).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      // const pdfHeight = pdf.internal.pageSize.getHeight(); // Not directly used for adding image parts
      const imgReportWidth = canvas.width;
      const imgReportHeight = canvas.height;
      const ratio = pdfWidth / imgReportWidth;
      const calculatedImgHeight = imgReportHeight * ratio;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, calculatedImgHeight);
      let heightLeft = calculatedImgHeight - pdf.internal.pageSize.getHeight();

      while (heightLeft > 0) {
        position -= pdf.internal.pageSize.getHeight();
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, calculatedImgHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }
      pdf.save(`reporte-clinico-${editablePatient.id}-${Date.now()}.pdf`);
    }).catch(err => {
        console.error("Error al generar PDF:", err);
        setError("Error al generar el PDF. Verifique la consola para más detalles.");
    });
  };

  const renderProgressBar = () => {
    const steps: WorkflowStep[] = ['patientInfo', 'symptoms', 'ncs', 'emg', 'analysis', 'report'];
    const currentIndex = steps.indexOf(currentStep);
    
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = stepIcons[step];
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;

            return (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors duration-300 ${
                  isCompleted 
                    ? 'bg-green-600 text-white' 
                    : isCurrent 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-700 text-gray-400'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium transition-colors duration-300 ${
                  isCurrent ? 'text-blue-400' : isCompleted ? 'text-green-400' : 'text-gray-400'
                }`}>
                  {stepTitles[step]}
                </span>
                {index < steps.length - 1 && (
                  <div className={`mx-4 h-1 flex-1 rounded ${
                    isCompleted ? 'bg-green-600' : 'bg-gray-700'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (isProcessing) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <h3 className="text-lg font-medium text-gray-200 mb-2">Procesando datos clínicos...</h3>
          <p className="text-gray-400 text-center">
            Generando reporte JSON y analizando patrones diagnósticos
          </p>
        </div>
      );
    }

    switch (currentStep) {
      case 'patientInfo':
        return (
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-100 mb-2">Datos Generales del Paciente</h2>
              <p className="text-gray-300">
                Verifique y corrija la información extraída antes de iniciar el cuestionario clínico.
              </p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700 p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-100 mb-4">Identificación</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">ID / Expediente</span>
                    <input
                      type="text"
                      value={editablePatient.id}
                      onChange={(event) => handlePatientFieldChange('id', event.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">ID externo</span>
                    <input
                      type="text"
                      value={editablePatient.patientId || ''}
                      onChange={(event) => handlePatientFieldChange('patientId', event.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Nombre(s)</span>
                    <input
                      type="text"
                      value={editablePatient.firstName}
                      onChange={(event) => handlePatientFieldChange('firstName', event.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Apellidos</span>
                    <input
                      type="text"
                      value={editablePatient.lastName}
                      onChange={(event) => handlePatientFieldChange('lastName', event.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-100 mb-4">Demografía Clínica</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Edad</span>
                    <input
                      type="number"
                      min="0"
                      value={editablePatient.age ?? ''}
                      onChange={(event) => handlePatientFieldChange('age', event.target.value === '' ? undefined : Number(event.target.value))}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Fecha de nacimiento</span>
                    <input
                      type="date"
                      value={editablePatient.dateOfBirth || ''}
                      onChange={(event) => handlePatientFieldChange('dateOfBirth', event.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Sexo</span>
                    <select
                      value={editablePatient.sex}
                      onChange={(event) => handlePatientFieldChange('sex', event.target.value as Sex)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="male">Masculino</option>
                      <option value="female">Femenino</option>
                      <option value="other">Otro / no especificado</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Peso (kg)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={editablePatient.weight ?? ''}
                      onChange={(event) => handlePatientFieldChange('weight', event.target.value === '' ? null : Number(event.target.value))}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Talla (cm)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={editablePatient.height ?? ''}
                      onChange={(event) => handlePatientFieldChange('height', event.target.value === '' ? null : Number(event.target.value))}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Mano dominante</span>
                    <select
                      value={editablePatient.handDominance || ''}
                      onChange={(event) => handlePatientFieldChange('handDominance', event.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">No especificada</option>
                      <option value="right">Derecha</option>
                      <option value="left">Izquierda</option>
                      <option value="ambidextrous">Ambidiestra</option>
                    </select>
                  </label>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-100 mb-4">Contexto Clínico</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Ocupación</span>
                    <input
                      type="text"
                      value={editablePatient.occupation || ''}
                      onChange={(event) => handlePatientFieldChange('occupation', event.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Diagnóstico principal sospechado</span>
                    <input
                      type="text"
                      value={editablePatient.mainDiagnosis || ''}
                      onChange={(event) => handlePatientFieldChange('mainDiagnosis', event.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Motivo de consulta / pregunta clínica</span>
                    <textarea
                      value={editablePatient.consultReason || ''}
                      onChange={(event) => handlePatientFieldChange('consultReason', event.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Antecedentes relevantes</span>
                    <textarea
                      value={(editablePatient.medicalHistory?.previousDiseases || []).join(', ')}
                      onChange={(event) => handleMedicalHistoryTextChange('previousDiseases', event.target.value)}
                      rows={2}
                      placeholder="Diabetes, hipotiroidismo, insuficiencia renal..."
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Cirugías relevantes</span>
                    <textarea
                      value={(editablePatient.medicalHistory?.surgeries || []).join(', ')}
                      onChange={(event) => handleMedicalHistoryTextChange('surgeries', event.target.value)}
                      rows={2}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Medicamentos actuales</span>
                    <textarea
                      value={(editablePatient.medicalHistory?.currentMedications || []).join(', ')}
                      onChange={(event) => handleMedicalHistoryTextChange('currentMedications', event.target.value)}
                      rows={2}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Alergias</span>
                    <textarea
                      value={(editablePatient.medicalHistory?.allergies || []).join(', ')}
                      onChange={(event) => handleMedicalHistoryTextChange('allergies', event.target.value)}
                      rows={2}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-100 mb-4">Contacto y Notas</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Teléfono</span>
                    <input
                      type="text"
                      value={editablePatient.contact?.phone || ''}
                      onChange={(event) => handleContactFieldChange('phone', event.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Correo</span>
                    <input
                      type="email"
                      value={editablePatient.contact?.email || ''}
                      onChange={(event) => handleContactFieldChange('email', event.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="block text-sm font-medium text-gray-300 mb-1">Notas adicionales</span>
                    <textarea
                      value={editablePatient.additionalNotes || ''}
                      onChange={(event) => handlePatientFieldChange('additionalNotes', event.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-gray-600 bg-gray-900 px-3 py-2 text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-between">
              <button
                onClick={onBack}
                className="px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 flex items-center transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />Volver
              </button>
              <button
                onClick={handlePatientInfoComplete}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
              >
                Continuar a Síntomas<ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        );

      case 'symptoms':
        // 🔥 PASAR DATOS INICIALES AL FORMULARIO DE SÍNTOMAS
        const initialSymptomsData = initialFormData?.symptoms?.data || undefined;
        
        console.log('📋 Pasando datos iniciales a ClinicalSymptomsForm:', {
          hasInitialData: !!initialSymptomsData,
          dataKeys: initialSymptomsData ? Object.keys(initialSymptomsData) : []
        });
        
        return (
          <div className="space-y-4">
            <ClinicalSymptomsForm 
              onComplete={handleSymptomsComplete} 
              onBack={() => setCurrentStep('patientInfo')}
              initialData={initialSymptomsData} // 🔥 DATOS AUTO-LLENADOS
            />
          </div>
        );

      case 'ncs':
        // 🔥 PREPARAR DATOS INICIALES PARA NCS AUTO-LLENADO
        const initialNCSData = initialFormData?.ncs ? {
          ncsResults: initialFormData.ncs.data || [],
          specialStudies: initialFormData.specialStudies?.data || { notPerformed: true, tests: [] }
        } : undefined;
        
        console.log('🔌 Pasando datos iniciales a NeuroConductionForm:', {
          hasInitialData: !!initialNCSData,
          ncsResultsCount: initialNCSData?.ncsResults?.length || 0,
          specialStudiesCount: initialNCSData?.specialStudies?.tests?.length || 0,
          rawInitialFormData: initialFormData,
          processedNCSData: initialNCSData,
          // 🔍 DEBUG: Mostrar estructura completa
          formDataStructure: {
            hasNcs: !!initialFormData?.ncs,
            ncsSuccess: initialFormData?.ncs?.success,
            ncsDataLength: initialFormData?.ncs?.data?.length || 0,
            hasEmg: !!initialFormData?.emg,
            emgSuccess: initialFormData?.emg?.success,
            emgDataLength: initialFormData?.emg?.data?.length || 0
          }
        });
        
        // 🔍 DEBUG: Mostrar estructura detallada de los datos NCS
        if (initialNCSData?.ncsResults) {
          console.log('📊 Datos NCS procesados para enviar:', initialNCSData.ncsResults);
          initialNCSData.ncsResults.forEach((ncs, index) => {
            console.log(`   NCS ${index + 1}:`, {
              id: ncs.id,
              nerve: ncs.nerve,
              side: ncs.side,
              type: ncs.type,
              latency: ncs.latency,
              amplitude: ncs.amplitude,
              velocity: ncs.velocity,
              status: ncs.status
            });
          });
        } else {
          console.warn('⚠️ No hay datos NCS para pasar al formulario');
        }
        
        return (
            <div className="max-w-4xl mx-auto space-y-4">
                <div className="mb-2">
                <h2 className="text-2xl font-bold text-gray-100 mb-2">Estudios de Neuroconducción</h2>
                <p className="text-gray-300">Complete los valores de velocidades, amplitudes y latencias de cada nervio estudiado.</p>
                </div>
                {thermalPrecisionWarning && (
                  <div className="mb-4 p-3 bg-yellow-900/40 border border-yellow-600 rounded-md flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-yellow-100 text-sm">{thermalPrecisionWarning}</p>
                  </div>
                )}
                {martinGruberNcsWarning && (
                  <div className="mb-4 p-3 bg-amber-900/30 border border-amber-600/60 rounded-md flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-amber-100 text-sm">{martinGruberNcsWarning}</p>
                  </div>
                )}
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700 p-1">
                    <NeuroConductionForm 
                      onSave={handleNCSComplete} 
                      onDraftChange={handleNcsDraftChange}
                      initialData={initialNCSData} // 🔥 DATOS AUTO-LLENADOS PARA NCS
                    />
                </div>
                <div className="mt-6 flex justify-start">
                    <button onClick={() => setCurrentStep('symptoms')} className="px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 flex items-center transition-colors"><ArrowLeft className="w-4 h-4 mr-2" />Volver a Síntomas</button>
                </div>
            </div>
        );

      case 'emg':
        // 🔥 PREPARAR DATOS INICIALES PARA EMG AUTO-LLENADO
        const initialEMGData = initialFormData?.emg?.data || undefined;
        
        console.log('🔬 Pasando datos iniciales a EMGNeedleAnalysis:', {
          hasInitialData: !!initialEMGData,
          emgRecordsCount: initialEMGData?.length || 0,
          muscles: initialEMGData?.map(record => `${record.muscleOrNerveName} (${record.side})`) || [],
          rawInitialFormData: initialFormData,
          processedEMGData: initialEMGData,
          // 🔍 DEBUG: Verificar estructura EMG
          emgStructure: {
            hasEmg: !!initialFormData?.emg,
            emgSuccess: initialFormData?.emg?.success,
            emgDataLength: initialFormData?.emg?.data?.length || 0,
            mappingStats: initialFormData?.emg?.mappingStats
          }
        });
        
        // 🔍 DEBUG: Mostrar estructura detallada de los datos EMG
        if (initialEMGData) {
          console.log('📊 Datos EMG procesados para enviar:', initialEMGData);
          initialEMGData.forEach((emg, index) => {
            console.log(`   EMG ${index + 1}:`, {
              id: emg.id,
              muscle: emg.muscleOrNerveName,
              side: emg.side,
              insertionalActivity: emg.insertionalActivity,
              recruitmentPattern: emg.recruitmentPattern,
              hasSpont: !!emg.spontaneousActivity,
              hasMUP: !!emg.motorUnitPotentials
            });
          });
        } else {
          console.warn('⚠️ No hay datos EMG para pasar al formulario');
        }
        
        return (
            <div className="max-w-4xl mx-auto space-y-4">
                <div className="mb-2">
                    <h2 className="text-2xl font-bold text-gray-100 mb-2">Electromiografía con Aguja</h2>
                    <p className="text-gray-300 mb-4">Complete los datos de electromiografía para generar el análisis diagnóstico, o indique si el estudio no se realizó.</p>
                    <button
                        onClick={handleEMGNotPerformed}
                        className="mb-2 flex items-center px-4 py-2 text-sm bg-yellow-600/80 text-white rounded-md hover:bg-yellow-700/80 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                        <XCircle className="w-4 h-4 mr-2" />
                        Marcar EMG como No Realizada y Continuar
                    </button>
                </div>
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700 p-1">
                    <EMGNeedleAnalysis 
                      onComplete={handleEMGComplete} 
                      initialData={initialEMGData} // 🔥 DATOS AUTO-LLENADOS PARA EMG
                    />
                </div>
                <div className="mt-6 flex justify-start">
                    <button onClick={() => setCurrentStep('ncs')} className="px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 flex items-center transition-colors"><ArrowLeft className="w-4 h-4 mr-2" />Volver a Neuroconducciones</button>
                </div>
            </div>
        );

      case 'analysis':
        if (!clinicalReport) {
            return <div className="text-center text-gray-400 py-10">Generando reporte para análisis...</div>;
        }
        return (
            <div className="max-w-6xl mx-auto">
                <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-100 mb-2">Análisis Diagnóstico</h2>
                <p className="text-gray-300">Resultados del análisis automático de patrones diagnósticos.</p>
                </div>
                {genieEvaluation?.topSuspicion && (
                  <div className="mb-6 bg-indigo-900/30 border border-indigo-600/50 p-4 rounded-md">
                    <div className="flex items-center text-indigo-300">
                      <Sparkles className="w-5 h-5 mr-2 flex-shrink-0" />
                      <span className="font-bold">Orientación del Genie</span>
                    </div>
                    <p className="text-sm text-indigo-100 mt-1">
                      Sospecha topográfica (síntomas + NCS + EMG):{' '}
                      <strong>{genieEvaluation.topSuspicion.label}</strong>{' '}
                      ({Math.round(genieEvaluation.topSuspicion.normalized * 100)}%).
                      {genieEvaluation.isAmbiguous &&
                        ' Diferencial estrecho: contraste con los patrones electrofisiológicos detectados.'}
                    </p>
                  </div>
                )}
                {getGenieWarnings(genieAnswers, {
                  liveDiagnoses,
                  evaluation: genieEvaluation ?? undefined,
                }).map(warning => (
                  <div key={warning} className="mb-6 bg-yellow-900/40 border border-yellow-600 p-4 rounded-md">
                    <div className="flex items-center text-yellow-400">
                      <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
                      <span className="font-bold">Aviso del Genie</span>
                    </div>
                    <p className="text-sm text-yellow-200 mt-1">{warning}</p>
                  </div>
                ))}
                {clinicalReport.electrophysiologicalData.ncs?.results &&
                  hasNcsLowTemperatureRecords(clinicalReport.electrophysiologicalData.ncs.results) && (
                  <div className="mb-6 bg-yellow-900/40 border border-yellow-600 p-4 rounded-md">
                    <div className="flex items-center text-yellow-400">
                      <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
                      <span className="font-bold">Aviso de precisión térmica</span>
                    </div>
                    <p className="text-sm text-yellow-200 mt-1">
                      Algunos NCS se registraron con temperatura cutánea bajo el estándar. El análisis puede incorporar correcciones;
                      valide resultados limítrofes recalentando la extremidad y repitiendo si es necesario.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-blue-900/30 border border-blue-700/50 p-4 rounded-lg">
                        <div className="flex items-center">
                        <Brain className="w-8 h-8 text-blue-400 mr-3" />
                        <div>
                            <h3 className="text-lg font-semibold text-blue-300">Patrones Identificados</h3>
                            <p className="text-2xl font-bold text-blue-400">{diagnosticPatterns.length}</p>
                        </div>
                        </div>
                    </div>
                    <div className="bg-green-900/30 border border-green-700/50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <CheckCircle className="w-8 h-8 text-green-400 mr-3" />
                            <div>
                            <h3 className="text-lg font-semibold text-green-300">Alta Confianza</h3>
                            <p className="text-2xl font-bold text-green-400">{diagnosticPatterns.filter(p => p.confidence > 0.8).length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-orange-900/30 border border-orange-700/50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <AlertTriangle className="w-8 h-8 text-orange-400 mr-3" />
                            <div>
                            <h3 className="text-lg font-semibold text-orange-300">Urgentes</h3>
                            <p className="text-2xl font-bold text-orange-400">{diagnosticPatterns.filter(p => p.urgencyLevel === 'urgent').length}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mb-8 bg-gray-800/50 backdrop-blur-sm p-6 rounded-lg border border-gray-700">
                    <h3 className="text-xl font-semibold text-gray-100 mb-4">Patrones Diagnósticos Detallados</h3>
                    {diagnosticPatterns.length > 0 ? (
                        <div className="space-y-4">
                        {diagnosticPatterns.map((pattern) => {
                            const genieCategory =
                              clinicalReport.genieOrientation?.topCategory ??
                              genieEvaluation?.topSuspicion?.category;
                            const isGenieCompatible =
                              !!genieCategory && patternMatchesSuspicion(pattern.id, genieCategory);
                            return (
                            <div
                              key={pattern.id}
                              className={`p-4 rounded-lg border ${
                                isGenieCompatible
                                  ? 'bg-indigo-900/30 border-indigo-500/60'
                                  : 'bg-gray-700/70 border-gray-600'
                              }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-lg font-medium text-gray-100">{pattern.name}</h4>
                                    {isGenieCompatible && (
                                      <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-200 bg-indigo-800/50 px-2 py-0.5 rounded-full">
                                        <Sparkles className="w-3 h-3" />
                                        Compatible con orientación clínica
                                      </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-300 mb-1">Relevancia Clínica: {(pattern.clinicalRelevance * 100).toFixed(0)}%</p>
                                <div className="text-sm text-gray-300">
                                    <strong className="text-gray-400">Evidencia de Apoyo:</strong>
                                    <ul className="list-disc list-inside pl-4 mt-1">
                                    {pattern.supportingEvidence.map((ev, i) => <li key={i}>{ev.description}</li>)}
                                    </ul>
                                </div>
                            </div>
                            );
                        })}
                        </div>
                    ) : <p className="text-gray-400">No se identificaron patrones diagnósticos específicos.</p>}
                </div>

                {(() => {
                  const activeTiebreakers = getActiveTiebreakers(diagnosticPatterns.map(p => p.id));
                  if (activeTiebreakers.length === 0) return null;
                  return (
                    <div className="mb-8 bg-amber-900/20 border border-amber-600/50 p-6 rounded-lg">
                      <h3 className="text-xl font-semibold text-amber-200 mb-1 flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        Preguntas de desempate
                      </h3>
                      <p className="text-sm text-amber-100/80 mb-4">
                        Se detectaron patrones en conflicto. Responda para modular la confianza
                        del diferencial crítico; la conducta sugerida aparece al seleccionar.
                      </p>
                      <div className="space-y-4">
                        {activeTiebreakers.map(tb => {
                          const selectedOptionId = genieAnswers[tb.id];
                          const selectedOption = tb.options.find(o => o.id === selectedOptionId);
                          return (
                            <div key={tb.id} className="bg-gray-800/60 p-4 rounded-md border border-amber-700/40">
                              <h4 className="font-medium text-amber-100">{tb.title}</h4>
                              <p className="text-sm text-gray-300 mt-1 mb-2">{tb.question}</p>
                              <div className="flex flex-wrap gap-2">
                                {tb.options.map(opt => {
                                  const selected = selectedOptionId === opt.id;
                                  return (
                                    <button
                                      key={opt.id}
                                      type="button"
                                      onClick={() => handleTiebreakerSelect(tb.id, opt.id)}
                                      className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                                        selected
                                          ? 'bg-amber-500 text-gray-900 border-amber-500'
                                          : 'bg-gray-700 text-gray-200 border-gray-500 hover:border-amber-400'
                                      }`}
                                    >
                                      {opt.label}
                                    </button>
                                  );
                                })}
                              </div>
                              {selectedOption && (
                                <p className="mt-3 text-sm text-amber-100 bg-amber-900/30 border border-amber-700/40 rounded p-2">
                                  {selectedOption.resolution}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {recommendations.length > 0 && (
                    <div className="mb-8 bg-gray-800/50 backdrop-blur-sm p-6 rounded-lg border border-gray-700">
                    <h3 className="text-xl font-semibold text-gray-100 mb-4">Recomendaciones Clínicas</h3>
                     <div className="space-y-3">
                        {recommendations.map((rec, i) => (
                            <div key={i} className="p-3 bg-gray-700/70 rounded-md border border-gray-600">
                                <h5 className="font-semibold text-blue-300">{rec.diagnosisName} (Confianza: {(rec.overallConfidence * 100).toFixed(0)}%)</h5>
                                <p className="text-sm text-gray-300"><strong className="text-gray-400">Pasos Siguientes:</strong> {rec.nextSteps.join(', ')}</p>
                                <p className="text-sm text-gray-300"><strong className="text-gray-400">Especialistas:</strong> {rec.specialists.join(', ')}</p>
                                <p className="text-sm text-gray-300"><strong className="text-gray-400">Tiempo Estimado:</strong> {rec.timeframe}</p>
                            </div>
                        ))}
                        </div>
                    </div>
                )}

                <div className="mb-8 bg-gray-800/50 backdrop-blur-sm p-6 rounded-lg border border-gray-700">
                  <h3 className="text-xl font-semibold text-gray-100 mb-4">
                    Consistencia NCS / EMG
                  </h3>
                  <PatternConsistencyPanel result={crossValidationResult} />
                </div>

                <div className="flex justify-between mt-8">
                    <button onClick={() => setCurrentStep('emg')} className="px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 flex items-center transition-colors"><ArrowLeft className="w-4 h-4 mr-2" />Volver a EMG</button>
                    <button onClick={handleAnalysisComplete} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"><ArrowRight className="w-4 h-4 ml-2" />Ver Reporte Final</button>
                </div>
            </div>
        );

      case 'report':
        const radiculopathyProtocolAlert =
          clinicalReport ? validateRadiculopathyProtocol(clinicalReport) : null;
        const reportQualityIssues =
          clinicalReport ? evaluateEdxReportQuality(clinicalReport) : [];

        return (
          <div className="max-w-5xl mx-auto">
            <div className="mb-6 text-center no-print">
              <h2 className="text-3xl font-bold text-gray-100 mb-1">Reporte de Electroneuromiografía</h2>
              <p className="text-gray-400">Resumen de la evaluación completa del paciente.</p>
            </div>

            {radiculopathyProtocolAlert && (
              <div className="mb-6 no-print bg-amber-900/35 border border-amber-600/70 rounded-lg p-4 flex gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-100">{radiculopathyProtocolAlert.message}</p>
              </div>
            )}

            {clinicalReport && crossValidationResult && crossValidationResult.conflicts.length > 0 && (
              <div className="mb-6 no-print bg-red-900/25 border border-red-700/50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-red-200 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Inconsistencias NCS/EMG detectadas
                </h3>
                <PatternConsistencyPanel result={crossValidationResult} />
              </div>
            )}

            {clinicalReport && <ReportQualityPanel issues={reportQualityIssues} />}

            <div className="flex flex-wrap justify-center sm:justify-end items-center gap-3 mb-6 no-print">
              <button
                onClick={downloadJsonReport}
                className="flex items-center px-4 py-2 text-sm bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 transition-colors"
              >
                <FileJson className="w-4 h-4 mr-2" />
                JSON
              </button>
              <button
                onClick={handleExportToTXT}
                className="flex items-center px-4 py-2 text-sm bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 transition-colors"
              >
                <FileTextIcon className="w-4 h-4 mr-2" />
                TXT
              </button>
              <button
                onClick={handleCopyClinicalSummary}
                className="flex items-center px-4 py-2 text-sm bg-blue-700 text-blue-50 rounded-md hover:bg-blue-600 transition-colors"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar resumen
              </button>
              <button
                onClick={handleExportToPDF}
                className="flex items-center px-4 py-2 text-sm bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 transition-colors"
              >
                <FileUp className="w-4 h-4 mr-2" /> 
                PDF
              </button>
              <button
                onClick={() => window.print()}
                className="flex items-center px-4 py-2 text-sm bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 transition-colors"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimir
              </button>
            </div>

            {clinicalReport ? (
              <div ref={reportContentRef} className="print-content">
                <PrintableEdxReport
                  report={clinicalReport}
                  patient={editablePatient}
                  analysis={getAnalysisResultForReport(clinicalReport)}
                />
                <div className="mt-8 no-print">
                  <FormattedReportDisplay report={clinicalReport} patient={editablePatient} />
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-400 py-10">No hay reporte clínico disponible para mostrar.</p>
            )}
            
            <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4 no-print">
              <button
                onClick={() => setCurrentStep('analysis')}
                className="w-full sm:w-auto px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 flex items-center justify-center transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al Análisis
              </button>
              <button
                onClick={handleWorkflowComplete}
                className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-purple-500 transition-colors"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Completar y Cerrar Evaluación
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 text-gray-100">
      <FloatingGeniePanel
        answers={genieAnswers}
        onChange={handleGenieChange}
        currentWindow={genieCurrentWindow}
        activeTiebreakers={activeTiebreakersForPanel}
        liveDiagnoses={liveDiagnoses}
        initialSuspicions={initialSuspicions}
        onInitialSuspicionsChange={setInitialSuspicions}
      />
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <User className="w-8 h-8 text-blue-400 mr-3" />
            <div>
              <h1 className="text-3xl font-bold">
                Evaluación Clínica Completa
              </h1>
              <p className="text-gray-300">
                Paciente: {editablePatient.firstName} {editablePatient.lastName} - ID: {editablePatient.id}
              </p>
            </div>
          </div>
          
          {renderProgressBar()}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-700/50 rounded-md">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
              <p className="text-red-300">{error}</p>
            </div>
          </div>
        )}

        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default CompleteClinicalWorkflow;
export type { WorkflowResult }; 